import { useSearchParams } from '@remix-run/react'
import { useState } from 'react'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Checkbox } from '#app/shared/ui/inputs/Checkbox.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { TransmissionEnum, TractionEnum } from '../../../../../types/enums'
import { RangeInput } from './rangeInput'

interface StaticFilters {
  tracciones: string[]
  cambios: string[]
}

const staticFilters: StaticFilters = {
  tracciones: Object.values(TractionEnum),
  cambios: Object.values(TransmissionEnum),
}

type FilterPotenciaCambioProps = {
  transmissionCounts: Record<string, number>
  tractionCounts: Record<string, number>
}

export const FilterPotenciaCambio = ({
  transmissionCounts,
  tractionCounts,
}: FilterPotenciaCambioProps): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isFilterOpen, setIsFilterOpen] = useState<
    'traccion' | 'cambio' | 'potencia' | null
  >(null)

  const handleCheckboxChange = (
    filterKey: string,
    value: string,
    checked: boolean,
  ) => {
    const currentFilters = searchParams.get(filterKey)?.split(',') || []
    let updatedFilters

    if (checked) {
      updatedFilters = [...currentFilters, value]
    } else {
      updatedFilters = currentFilters.filter((f: string) => f !== value)
    }

    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      [filterKey]: updatedFilters.join(','),
    })
  }

  const CvSuffix = () => <div className="text-foreground-muted">CV</div>

  return (
    <MainContainer>
      <div
        className={`
          flex flex-col

          lg:flex-row lg:gap-32
        `}
      >
        <div>
          <button
            className={`
              flex w-full items-center justify-between border-t py-4

              lg:hidden
            `}
            onClick={() =>
              setIsFilterOpen(isFilterOpen === 'cambio' ? null : 'cambio')
            }
          >
            Cambio
            <Icon
              name="basic/chevron-down"
              className={`
                h-5 w-5 text-neutral-400 transition duration-200

                ${isFilterOpen === 'cambio' && 'rotate-180'}
              `}
            />
          </button>
          {(isFilterOpen === 'cambio' || window.innerWidth >= 1024) && (
            <div>
              {window.innerWidth >= 1024 && (
                <h3 className="mb-4 mt-4 text-base-bold">Cambio</h3>
              )}
              <div className="mb-4 flex flex-col gap-3">
                {staticFilters.cambios.map((cambio) => {
                  const count = transmissionCounts[cambio] ?? 0
                  const isDisabled = count === 0
                  return (
                    <div key={cambio} className="flex items-center gap-2">
                      <Checkbox
                        id={cambio}
                        checked={
                          searchParams
                            .get('cambio')
                            ?.split(',')
                            .includes(cambio) || false
                        }
                        disabled={isDisabled}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange('cambio', cambio, !!checked)
                        }
                      />
                      <label
                        htmlFor={cambio}
                        className={count === 0 ? 'text-gray-400' : ''}
                      >
                        {cambio} ({count})
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div>
          <button
            className={`
              flex w-full items-center justify-between border-t py-4

              lg:hidden
            `}
            onClick={() =>
              setIsFilterOpen(isFilterOpen === 'traccion' ? null : 'traccion')
            }
          >
            Tracción
            <Icon
              name="basic/chevron-down"
              className={`
                h-5 w-5 text-neutral-400 transition duration-200

                ${isFilterOpen === 'traccion' && 'rotate-180'}
              `}
            />
          </button>
          {(isFilterOpen === 'traccion' || window.innerWidth >= 1024) && (
            <div>
              {window.innerWidth >= 1024 && (
                <h3 className="mb-4 mt-4 text-base-bold">Tracción</h3>
              )}
              <div className="mb-4 flex flex-col gap-3">
                {staticFilters.tracciones.map((traccion) => {
                  const count = tractionCounts[traccion] ?? 0
                  const isDisabled = count === 0
                  return (
                    <div key={traccion} className="flex items-center gap-2">
                      <Checkbox
                        id={traccion}
                        checked={
                          searchParams
                            .get('traccion')
                            ?.split(',')
                            .includes(traccion) || false
                        }
                        disabled={isDisabled}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange('traccion', traccion, !!checked)
                        }
                      />
                      <label
                        htmlFor={traccion}
                        className={count === 0 ? 'text-gray-400' : ''}
                      >
                        {traccion} ({count})
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div>
          <button
            className={`
              flex w-full items-center justify-between border-t py-4

              lg:hidden
            `}
            onClick={() =>
              setIsFilterOpen(isFilterOpen === 'potencia' ? null : 'potencia')
            }
          >
            Potencia (CV)
            <Icon
              name="basic/chevron-down"
              className={`
                h-5 w-5 text-neutral-400 transition duration-200

                ${isFilterOpen === 'potencia' && 'rotate-180'}
              `}
            />
          </button>
          {(isFilterOpen === 'potencia' || window.innerWidth >= 1024) && (
            <div className="mb-4 mt-4">
              {window.innerWidth >= 1024 && (
                <RangeInput
                  title="Potencia (CV)"
                  minParamName="powerMin"
                  maxParamName="powerMax"
                  suffix={<CvSuffix />}
                />
              )}
              {window.innerWidth < 1024 && (
                <div className="mt-4">
                  <RangeInput
                    title=""
                    minParamName="powerMin"
                    maxParamName="powerMax"
                    suffix={<CvSuffix />}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  )
}
